import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import { SessionStore, UserInfo, OemDealers, Role, Permission } from './session.store';
import { environment } from 'src/environments/environment';
import { tap, catchError, map, take, mergeMap, switchMap } from 'rxjs/operators';
import { throwError, Observable, of, from, EMPTY } from 'rxjs';
import { applyTransaction } from '@datorama/akita';
import { Router } from '@angular/router';
import {
  LoginStatus,
  PasswordRegistrationResponse,
  PasswordRegistrationRequest,
  DefaultStatus,
  CheckUserOptinResponse,
  AcceptNewLoginTermsRequest
} from 'src/app/shared/model/login';
import { ToastrService } from 'ngx-toastr';
import { InterceptorSkipDealerIdHeader, InterceptorSkipHeader, InterceptorSkipOemDealerIdHeader } from 'src/app/shared/guards/skip.interceptor';
import { AmplifyService } from '@app-services/amplify.service';
import { BlipChatService } from '@app-services/blip-chat.service';
import { DefaultMessage } from '@app/views/private/admin/services/user-manager.service';
import { IdName } from '@app-services/oem-dealer.service';
import { ValueConfig } from '@app/views/private/admin/model/advertisers';

export enum MigrationStatusReason {
  SHOULD_SHOW = 'SHOULD_SHOW',
  NOT_ELIGIBLE = 'NOT_ELIGIBLE',
  ALREADY_MIGRATED = 'ALREADY_MIGRATED',
  MANDATORY_MIGRATION = 'MANDATORY_MIGRATION',
  ALREADY_SHOWN_TODAY = 'ALREADY_SHOWN_TODAY'
}

export interface MigrationStatus {
  shouldShowModal: boolean;
  isMigrationMandatory: boolean;
  statusReason: MigrationStatusReason;
  mandatoryDate: string;
}

@Injectable({
  providedIn: 'root'
})
export class SessionService {

  forcedJwt: string;

  constructor(
    private sessionStore: SessionStore,
    private http: HttpClient,
    private amplifyService: AmplifyService,
    private blipChatService: BlipChatService,
    private router: Router,
    private toastr: ToastrService
  ) { }


  loginAuthorization(accessToken: string, refreshToken: string, expiresIn: number, returnUrl?: string): void {
    this.setLoading(true);
    this.setTokenInfo(accessToken, refreshToken, expiresIn);

    localStorage.setItem('showSuperBidPendingOnce', Date.now().toString());
    this.setAuthenticatedAndRedirect(returnUrl);
  }

  private setTokenInfo(accessToken: string, refreshToken: string, expiresIn: number) {
    const expiresAt = new Date().getTime() + (1000 * (expiresIn - 120));
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('expiresAt', expiresAt.toString());

  }

  refreshToken(): Observable<string> {
    const header = new HttpHeaders()
      .set('Content-Type', 'application/x-www-form-urlencoded')
      .set(InterceptorSkipHeader, '')
      .set(InterceptorSkipOemDealerIdHeader, '')
      .set(InterceptorSkipDealerIdHeader, '')
      ;


    const refreshToken = localStorage.getItem('refreshToken');
    const body = `grant_type=refresh_token&client_id=${environment.superBidClientId}&refresh_token=${refreshToken}`;

    return this.http
      .post(`${environment.superBidRefreshTokenUrl}`, body, { headers: header })
      .pipe(
        take(1),
        map(response => {
          this.setTokenInfo(response['access_token'], response['refresh_token'], response['expires_in']);
          return response['access_token'];
        }),
        catchError(err => {
          localStorage.clear();
          this.sessionStore.clearSession();
          this.setLoading(false);
          return throwError(err);
        })
      );
  }

  getAuthToken(): Observable<string> {
    return this.amplifyService.isAuthenticated().pipe(
      switchMap(isAuthenticated => {
        if (isAuthenticated) {
          return this.amplifyService.getJwt().pipe(
            switchMap(jwt => jwt ? of(jwt) : throwError(() => new Error('No auth token')))
          );
        }
        if (localStorage.getItem('accessToken')) {
          return this.getSbxAuthToken();
        }
        return throwError(() => new Error('No auth token'));
      })
    );
  }

  private getSbxAuthToken(): Observable<string> {
    const expiresAt = Number.parseInt(localStorage.getItem('expiresAt') || '0');
    if (expiresAt < new Date().getTime()) {
      return this.refreshToken();
    }
    return of(localStorage.getItem('accessToken'));
  }

  login(username: string, password: string, returnUrl?: string): Promise<string> {
    this.setLoading(true);

    return this.amplifyService.login(username, password)
      .then(resp => {
        this.updateIsAuthenticated(true);
        localStorage.setItem('showSuperBidPendingOnce', Date.now().toString());
        this.setLoading(true);
        this.checkMigrationAndRedirect(returnUrl);

        /* if (!returnUrl || returnUrl === '/') {
          this.getUser().subscribe(u => {
            if (u.hasRoles(Role.ADMIN)) {
              returnUrl = '/vender/veiculos';
            } else if (u.hasRoles(Role.DESK_SELL, Role.OEM_DEALER_MANAGER, Role.REGISTERER, Role.LOCATION_MANAGER)) {
              returnUrl = '/vender/veiculos';
            } else if (u.hasRoles(Role.DESK_BUY, Role.EVALUATOR, Role.SELLER, Role.SELLER_MANAGER, Role.EVAL_DEALER_MANAGER, Role.EVALUATOR)) {
              returnUrl = '/captar/veiculos';
            } else {
              returnUrl = '/';
            }
            this.router.navigateByUrl(returnUrl);
          });

        } else {
          this.router.navigateByUrl(returnUrl);
        } */

        return undefined;
      }).catch(error => {
        this.setLoading(false);

        const message = 'E-mail ou senha inválidos';

        this.toastr.error('', message, {
          closeButton: true,
          progressBar: true,
          positionClass: 'toast-bottom-center',
          extendedTimeOut: 5000,
          timeOut: 10000
        });

        return message;
      });

  }

  checkMigrationAndRedirect(returnUrl?: string): void {
    this.getUser().subscribe(userInfo => {
      if (!userInfo) {
        this.performRedirect(returnUrl);
        return;
      }

      if (userInfo.hasRole(Role.DEALER) && userInfo.status === 'WAITING') {
        this.router.navigateByUrl('/dealer-unauthorized?source=platform');
        this.setLoading(false);
        return;
      }

      if (userInfo.migrationMandatoryDate) {
        setTimeout(() => {
          this.performRedirect(returnUrl);
        }, 5000);
      } else {
        // Redirecionar imediatamente se não há mensagem de migração
        this.performRedirect(returnUrl);
      }
    });
  }

  private performRedirect(returnUrl?: string): void {

    if (!returnUrl || returnUrl === '/') {
      this.getUser().subscribe(u => {
        returnUrl = this.resolveDefaultLandingRoute(u);
        this.router.navigateByUrl(returnUrl)
          .then(() => {
            this.setLoading(false);
          });
      });
    } else {
      this.router.navigateByUrl(returnUrl)
        .then(() => {
          this.setLoading(false);
        });
    }
  }

  setAuthenticatedAndRedirect(returnUrl?: string): void {
    this.updateIsAuthenticated(true);
    this.setLoading(true);

    if (!returnUrl || returnUrl === '/') {
      this.getUser().subscribe(u => {
        returnUrl = this.resolveDefaultLandingRoute(u);

        this.router.navigateByUrl(returnUrl)
          .then(() => {
            this.setLoading(false);
          });
      });

    } else {
      this.router.navigateByUrl(returnUrl)
        .then(() => {
          this.setLoading(false);
        });
    }

  }

  setLoading(status: boolean) {
    this.sessionStore.setLoading(status);
  }

  private resolveDefaultLandingRoute(user: UserInfo): string {
    if (user.hasPermission(Permission.DASHBOARD_MAIN_PAGE)) {
      return '/dashboard/eval';
    }

    if (user.hasPermissionOemDealerManager() && user.hasOemDealerContext()) {
      const oemDealerAdminRoute = user.getDefaultOemDealerAdminRoute();
      if (oemDealerAdminRoute) {
        return oemDealerAdminRoute;
      }
    }

    if (user.hasRoles(
      Role.DESK_BUY,
      Role.EVALUATOR,
      Role.SELLER,
      Role.SELLER_MANAGER,
      Role.EVAL_DEALER_MANAGER,
      Role.SUPER_EVALUATOR,
    ) || user.hasAnyBuyPermission()) {
      return '/captar/veiculos';
    }

    const sellRoute = user.getDefaultSellRoute();
    if (sellRoute) {
      return sellRoute;
    }

    const platformAdminRoute = user.getDefaultPlatformAdminRoute();
    if (platformAdminRoute) {
      return platformAdminRoute;
    }

    if (user.isDealer()) {
      return '/arrematar/veiculos';
    }

    if (user.hasOemDealerContext()) {
      return '/captar/veiculos';
    }

    return '/';
  }

  checkEmailStatus(email: string, turnstileToken?: string): Observable<LoginStatus> {
    this.setLoading(true);
    const payload: { email: string; turnstileToken?: string } = { email };
    if (turnstileToken) {
      payload.turnstileToken = turnstileToken;
    }
    return this.http.post(`${environment.api}/login/status`, payload).pipe(
      map((response: LoginStatus) => response),
      tap(() => this.sessionStore.setLoading(false)),
      catchError(err => {
        this.setLoading(false);
        return throwError(err);
      })
    );
  }

  sendSMS(email: string): Observable<DefaultStatus> {
    this.setLoading(true);
    return this.http.post(`${environment.api}/login/resetPasswordSms`, { email }).pipe(
      map((response: DefaultStatus) => response),
      tap(() => this.sessionStore.setLoading(false)),
      catchError(err => {
        this.setLoading(false);
        return throwError(err);
      })
    );
  }

  sendWhatsapp(email: string): Observable<DefaultStatus> {
    this.setLoading(true);
    return this.http.post(`${environment.api}/login/resetPasswordWhatsapp`, { email }).pipe(
      map((response: DefaultStatus) => response),
      tap(() => this.sessionStore.setLoading(false)),
      catchError(err => {
        this.setLoading(false);
        return throwError(err);
      })
    );
  }

  createUser(email: string) {
    this.sessionStore.setLoading(true);
    return this.http.post(`${environment.api}/login/createUser`, { email }).pipe(
      take(1),
      map((response: PasswordRegistrationResponse) =>
        Object.assign(new PasswordRegistrationResponse(), response)
      ),
      tap(() => {
        this.sessionStore.setLoading(false);
      }),
      catchError(err => {
        this.setLoading(false);
        return throwError(err);
      })
    );
  }

  resetPassword(
    userId: string,
    verifCode: string,
    password: string
  ): Observable<PasswordRegistrationResponse> {
    this.setLoading(true);
    const request: PasswordRegistrationRequest = {
      userId,
      verifCode,
      password
    };
    return this.http.post(`${environment.api}/login/resetPassword`, request).pipe(
      map((response: PasswordRegistrationResponse) =>
        Object.assign(new PasswordRegistrationResponse(), response)
      ),
      tap(() => {
        this.sessionStore.setLoading(false);
      }),
      catchError(err => {
        this.setLoading(false);
        return throwError(err);
      })
    );
  }

  verifyUser(userId: string, verifCode: string): Observable<PasswordRegistrationResponse> {
    return this.http
      .get(`${environment.api}/login/verifyUser/${userId}/${verifCode}`)
      .pipe(
        map((response: PasswordRegistrationResponse) =>
          Object.assign(new PasswordRegistrationResponse(), response)
        )
      );
  }


  checkDealerOptin(): Observable<CheckUserOptinResponse> {
    return this.http
      .get<CheckUserOptinResponse>(`${environment.api}/login/checkUserOptin`);
  }


  addDealerUserWithOptin(appClientId: string, email: string, name: string, cpf: string): Observable<DefaultMessage> {
    let queryParams = {};
    if (appClientId) {
      queryParams['appClientId'] = appClientId;
    }
    if (email) {
      queryParams['email'] = email;
    }
    if (name) {
      queryParams['name'] = name;
    }
    if (cpf) {
      queryParams['cpf'] = parseInt(cpf, 10).toString();
    }

    return this.http
      .get<DefaultMessage>(`${environment.api}/login/addDealerUserWithOptin`, { params: queryParams });
  }

  forgotPassword(email: string) {
    this.setLoading(true);
    return this.http.post(`${environment.api}/login/send-reset-password`, { email }).pipe(
      take(1),
      tap(() => {
        this.sessionStore.setLoading(false);
      }),
      catchError(err => {
        this.setLoading(false);
        return throwError(err);
      })
    );
  }

  isAuthenticated(): Observable<boolean> {
    return from(this.amplifyService.isAuthenticated()).pipe(
      map(isAuthenticated => {
        if (!isAuthenticated) {
          isAuthenticated = localStorage.getItem('accessToken') !== null;
        }

        this.sessionStore.update({ isAuthenticated });
        return isAuthenticated;
      })
    );
  }

  updateIsAuthenticated(isAuthenticated: boolean) {
    applyTransaction(() => {
      this.sessionStore.setLoading(false);
      this.sessionStore.update({ isAuthenticated });
    });
  }

  logout() {
    this.blipChatService.destroy();
    this.amplifyService.logout();
    localStorage.clear();
    this.sessionStore.clearSession();
  }

  registerUser(
    userId: string,
    verifCode: string,
    values
  ): Observable<PasswordRegistrationResponse> {
    return this.http
      .post(`${environment.api}/login/registerNewUser/${userId}/${verifCode}`, values)
      .pipe(
        take(1),
        map((response: PasswordRegistrationResponse) => response),
        tap(() => {
          this.sessionStore.setLoading(false);
        }),
        catchError(err => {
          this.setLoading(false);
          return throwError(err);
        })
      );
  }

  getUser(): Observable<UserInfo> {
    return this.isAuthenticated().pipe(
      mergeMap(isAuthenticated => {
        if (!isAuthenticated) {
          return of(undefined);
        }
        return this.sessionStore
          ._select(state => state.userInfo)
          .pipe(
            mergeMap(userInfo => {
              if (userInfo) {
                return of(userInfo);
              }
              return this.getUserFromBack();
            }),
            take(1)
          );
      })
    );
  }

  getUserFromBack(): Observable<UserInfo> {
    return this.http
      .get(`${environment.api}/user/info`, {
        // headers: new HttpHeaders().set(InterceptorSkipOemDealerIdHeader, '')
      })
      .pipe(
        map((userInfo: UserInfo) => {
          const mapped = Object.assign(new UserInfo(), userInfo);
          mapped.segmentComissionConfigs = toSegmentComissionConfigMap(userInfo.segmentComissionConfigs);
          return mapped;
        }),
        tap(userInfo => {
          if (userInfo.oemDealers && userInfo.oemDealers.length > 0) {
            userInfo.oemDealerGroupSelected = this.getOemDealerSelected();
          } else {
            localStorage.removeItem('oemDealerSelected');
            userInfo.oemDealerGroupSelected = { id: userInfo.oemDealerId, name: userInfo.oemDealerName, inactive: false, allowLeadGeneration: userInfo.showAuctionLeads };
          }

          if (userInfo.dealers && userInfo.dealers.length > 0) {
            userInfo.dealerGroupSelected = this.getDealerSelected();
          } else {
            localStorage.removeItem('dealerSelected');
            userInfo.dealerGroupSelected = { id: userInfo.dealerId, name: userInfo.dealerName };
          }

          this.sessionStore.update({ userInfo });
          globalThis.dataLayer.push({
            dealerName: userInfo.dealerName,
            dealerId: userInfo.dealerId,
            oemDealerName: userInfo.oemDealerName,
            oemDealerId: userInfo.oemDealerId,
            userId: userInfo.id
          });
        }),
        catchError(err => {
          if (err.error?.errorCode === 'superbid-account-type-mismatch') {
            const message = err.error?.message || 'A conta Wallet SBX informada não é compatível com seu cadastro.';

            this.logout();
            this.setLoading(false);
            this.router.navigate(['/login'], {
              queryParams: { authError: message }
            });

            return EMPTY;
          }

          return throwError(() => err);
        })
      );
  }

  getOemDealerSelected(): OemDealers | null {
    try {
      return JSON.parse(localStorage.getItem('oemDealerSelected'));
    } catch (err) {
      return null;
    }
  }

  getDealerSelected(): IdName | null {
    try {
      const selected = JSON.parse(localStorage.getItem('dealerSelected'))
      if (window.location.hostname.includes('mercedesbenz')) {
      }
      return selected;
    } catch (e) {
      console.error('Error parsing dealerSelected from localStorage', e);
      return null;
    }
  }

  updateStatusPlan(status) {
    this.sessionStore.updateStatusPlan(status);
  }

  updateOemDealersSelected(oemDealerSelected) {
    localStorage.setItem(
      'oemDealerSelected',
      oemDealerSelected ? JSON.stringify(oemDealerSelected) : null
    );

      this.sessionStore.updateOemDealersSelected(oemDealerSelected);
  }
  
  updateDealersSelected(dealerSelected: IdName) {
    localStorage.setItem(
      'dealerSelected',
      dealerSelected ? JSON.stringify(dealerSelected) : null
    );

    this.http.get<boolean>(`${environment.api}/user/hasLatePayment`).subscribe(resp => {
      this.sessionStore.updateDealersSelected(dealerSelected, resp);
    })
  }

  acceptAutomaticImportTerms(): Observable<DefaultMessage> {
    return this.http.put<DefaultMessage>(`${environment.api}/user/acceptAutomaticImportTerms`, {});
  }

  acceptNewLoginTerms(data: AcceptNewLoginTermsRequest): Observable<DefaultMessage> {
    return this.http.post<DefaultMessage>(`${environment.api}/user/acceptNewLogginTerms`, data);
  }

}

function toSegmentComissionConfigMap(
  raw?: Map<string, ValueConfig> | Record<string, ValueConfig> | null,
): Map<string, ValueConfig> | undefined {
  if (!raw) {
    return undefined;
  }

  if (raw instanceof Map) {
    return raw;
  }

  const mapped = new Map<string, ValueConfig>();
  for (const key of Object.keys(raw)) {
    mapped.set(key, Object.assign(new ValueConfig(), raw[key]));
  }
  return mapped;
}
